import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, message, Image, Space } from 'antd';
import { t } from '@/utils/translator';
import LanguageSwitcher from '@common/LanguageSwitcher';
import { DeleteOutlined } from '@ant-design/icons';
import API from '@/api/api';

const IMAGE_TYPE_TO_FIELD = {
  LANDSCAPE: 'site_overview_photo',
  FACILITY: 'site_layout_photo',
  PROCESS: 'site_process_photo',
};

const IMAGE_TYPE_MAP = {
  site_overview_photo: 'LANDSCAPE',
  site_layout_photo: 'FACILITY',
  site_process_photo: 'PROCESS',
};

const SitePhotoModal = ({ 
  isVisible, 
  form, 
  onCancel, 
  onClose, 
  estimateSiteInfoId,
  onUploadSuccess,
  requiredForm = { site_overview_photo: false, site_layout_photo: false, site_process_photo: false } 
}) => {
  const [photoFiles, setPhotoFiles] = useState({
    site_overview_photo: null,
    site_layout_photo: null,
    site_process_photo: null,
  });
  const [uploadedImages, setUploadedImages] = useState({
    site_overview_photo: null,
    site_layout_photo: null,
    site_process_photo: null,
  });
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({
    site_overview_photo: null,
    site_layout_photo: null,
    site_process_photo: null,
  });
  
  // 초기화용 파일 input key 
  const [fileInputKeys, setFileInputKeys] = useState({
    site_overview_photo: 0,
    site_layout_photo: 0,
    site_process_photo: 0,
  });

  // 업로드된 이미지 조회
  const fetchUploadedImages = useCallback(async () => {
    if (!estimateSiteInfoId) return;

    try {
      setIsLoadingImages(true);
      const siteInfoId = parseInt(estimateSiteInfoId, 10);
      const response = await API.get(`/admin/estimates/sites/${siteInfoId}/images`);
      
      if (response.data && response.data.images) {
        const imagesMap = {
          site_overview_photo: null,
          site_layout_photo: null,
          site_process_photo: null,
        };

        response.data.images.forEach((imageData) => {
          const fieldName = IMAGE_TYPE_TO_FIELD[imageData.image_type];
          if (fieldName) {
            // {image_type: 'LANDSCAPE', images: Array(1)}
            const imageInfo = imageData.images && imageData.images.length > 0 
              ? imageData.images[0] // 배열의 첫 번째 이미지 사용
              : imageData; // 직접 이미지 정보인 경우
            
            imagesMap[fieldName] = {
              ...imageData,
              ...imageInfo,
            };
          }
        });

        setUploadedImages(imagesMap);
      }
    } catch (error) {

    } finally {
      setIsLoadingImages(false);
    }
  }, [estimateSiteInfoId]);

  useEffect(() => {
    if (estimateSiteInfoId && isVisible) {
      fetchUploadedImages();
    }
  }, [estimateSiteInfoId, isVisible, fetchUploadedImages]);

  // uploadedImages가 업데이트되면 업로드된 파일들에 해당하는 photoFiles 초기화
  useEffect(() => {
    // uploadedImages가 비어있거나 초기 상태면 실행하지 않음
    if (!uploadedImages || Object.values(uploadedImages).every(img => img === null)) {
      return;
    }

    setPhotoFiles(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      Object.keys(prev).forEach(fieldName => {
        // 서버에 업로드된 이미지가 있고 해당 필드에 선택한 파일이 있으면 초기화
        if (uploadedImages[fieldName] && prev[fieldName]) {
          updated[fieldName] = null;
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
  }, [uploadedImages]);

  // 이미지 URL 생성 및 메모리 정리
  useEffect(() => {
    const newUrls = {};
    const oldUrls = [];

    // 이전 URL 저장
    Object.values(previewUrls).forEach(url => {
      if (url) oldUrls.push(url);
    });

    // 새 URL 생성
    Object.entries(photoFiles).forEach(([fieldName, file]) => {
      if (file) {
        newUrls[fieldName] = URL.createObjectURL(file);
      } else {
        newUrls[fieldName] = null;
      }
    });

    setPreviewUrls(newUrls);

    // 이전 URL들 정리
    return () => {
      oldUrls.forEach(url => URL.revokeObjectURL(url));
      Object.values(newUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [photoFiles]);

  // 파일 선택 핸들러
  const handleFileChange = (fieldName, file) => {
    setPhotoFiles(prev => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  // 파일 크기 검증
  const validateFileSize = (file, fieldName) => {
    const maxSize = 5 * 1024 * 1024;
    if (file && file.size > maxSize) {
      message.error(t('파일 크기는 5MB를 초과할 수 없습니다.'));
      return false;
    }
    return true;
  };

  // 이미지 삭제
  const handleDeleteImage = async (fieldName) => {
    try {
      const siteInfoId = parseInt(estimateSiteInfoId, 10);
      if (isNaN(siteInfoId)) {
        message.error('유효하지 않은 사업장 ID입니다.');
        return;
      }

      const imageType = IMAGE_TYPE_MAP[fieldName];
      await API.delete(`/admin/estimates/sites/${siteInfoId}/images`, {
        params: { image_type: imageType },
      });
      
      message.success('이미지가 삭제되었습니다.');
      await fetchUploadedImages();
    } catch (error) {
      message.error('이미지 삭제 중 오류가 발생했습니다.');
    }
  };

  // 업로드된 이미지 모두 삭제 
  const handleReset = async () => {
    try {
      const siteInfoId = parseInt(estimateSiteInfoId, 10);
      if (!isNaN(siteInfoId) && Object.values(uploadedImages).some(img => img !== null)) {
        const deletePromises = [];
        for (const [fieldName, image] of Object.entries(uploadedImages)) {
          if (image) {
            const imageType = IMAGE_TYPE_MAP[fieldName];
            deletePromises.push(
              API.delete(`/admin/estimates/sites/${siteInfoId}/images`, {
                params: { image_type: imageType },
              })
            );
          }
        }
        
        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
          message.success('업로드된 이미지가 모두 삭제되었습니다.');
          await fetchUploadedImages();
        }
      }
    } catch (error) {
      message.error('이미지 삭제 중 오류가 발생했습니다.');
    }
    
    // 입력 상태 초기화
    form.resetFields();
    setPhotoFiles({
      site_overview_photo: null,
      site_layout_photo: null,
      site_process_photo: null,
    });
    onClose();
  };
  
  // 저장 핸들러
  const handleSave = async () => {
    try {
      const hasFiles = Object.values(photoFiles).some(file => file !== null);
      if (!hasFiles) {
        message.warning('업로드할 사진을 선택해주세요.');
        return;
      }

      const siteInfoId = parseInt(estimateSiteInfoId, 10);
      if (isNaN(siteInfoId)) {
        message.error('유효하지 않은 사업장 ID입니다.');
        return;
      }

      const uploadPromises = [];
      for (const [fieldName, file] of Object.entries(photoFiles)) {
        if (file) {
          const imageType = IMAGE_TYPE_MAP[fieldName];
          const formData = new FormData();
          formData.append('file', file);
          formData.append('image_type', imageType);

          uploadPromises.push(
            API.post(`/admin/estimates/sites/${siteInfoId}/images`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }).catch((error) => {
              throw error;
            })
          );
        }
      }
      
      await Promise.all(uploadPromises);
      message.success('사진 업로드가 완료되었습니다.');
      
      // 서버에서 업로드된 이미지 조회
      // uploadedImages가 업데이트되면 useEffect에서 자동으로 photoFiles 초기화됨
      await fetchUploadedImages();
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      let errorMessage = '사진 업로드 중 오류가 발생했습니다.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 413) {
        errorMessage = '파일 크기가 너무 큽니다.';
      } else if (error.response?.status === 415) {
        errorMessage = '지원되지 않는 파일 형식입니다.';
      } else if (error.response?.status === 422) {
        errorMessage = '요청 데이터 형식이 올바르지 않습니다.';
      }
      message.error(errorMessage);
    }
  };
  return (
    <Modal
      title={t('사업장 사진 업로드')}
      open={isVisible}
      onOk={onClose}
      onCancel={onClose}
      footer={[
        <div key="footer" className="d-flex justify-content-between">
          <LanguageSwitcher />
          <div className="text-right">
            <Button type="danger" onClick={handleReset}>
              {t('업로드 파일 삭제')}
            </Button>
            <Button
              className="bg-gray-3 text-gray-1 hover-bg-gray-2 hover-border-gray-2 hover-text-gray-1"
              onClick={onCancel}
            >
              {t('닫기')}
            </Button>
            <Button type="primary" onClick={handleSave}>
              {t('저장')}
            </Button>
          </div>
        </div>,
      ]}
      centered
    >
      <Form
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 15 }}
        initialValues={{ remember: true }}
        autoComplete="off"
        className="py-2"
        labelAlign="left"
        labelWrap
      >
        <Form.Item
          label={t('사업장 전체 사진 (조감도)')}
          name="site_overview_photo"
          className="mb-3"
          rules={[{ required: requiredForm.site_overview_photo, message: t('이미지 파일을 선택해주세요') }]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {/* 새로 선택한 파일 프리뷰 */}
            {photoFiles.site_overview_photo && (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={previewUrls.site_overview_photo}
                  alt="선택한 사진"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview
                />
                <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                  {photoFiles.site_overview_photo.name}
                </span>
              </div>
            )}
            {/* 서버에 업로드된 이미지 프리뷰 */}
            {(() => {
              const hasLocalFile = !!photoFiles.site_overview_photo;
              const hasUploadedImage = !!uploadedImages.site_overview_photo;
              const shouldShowServerImage = !hasLocalFile && hasUploadedImage;
              
              return shouldShowServerImage ? (
                <div style={{ marginBottom: 8 }}>
                  <Image
                    src={
                      (() => {
                        const image = uploadedImages.site_overview_photo;
                        const s3Key = image?.s3_key || 
                                     (image?.images && image.images[0]?.s3_key);
                        return s3Key 
                          ? `${import.meta.env.VITE_STATIC_URL}/${s3Key}`
                          : image?.image_url || image?.url || '';
                      })()
                    }
                    alt="사업장 전체 사진"
                    width={200}
                    height={150}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    preview
                  />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleDeleteImage('site_overview_photo')}
                  style={{ marginLeft: 8 }}
                >
                  {t('삭제')}
                </Button>
              </div>
              ) : null;
            })()}
            <Input 
              key={fileInputKeys.site_overview_photo}
              type="file" 
              accept=".jpg,.jpeg,.png" 
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && validateFileSize(file, 'site_overview_photo')) {
                  handleFileChange('site_overview_photo', file);
                } else if (file) {
                  // 파일 크기 초과 시 input 초기화
                  handleFileChange('site_overview_photo', null);
                  setFileInputKeys(prev => ({
                    ...prev,
                    site_overview_photo: prev.site_overview_photo + 1,
                  }));
                }
              }} 
            />
          </Space>
        </Form.Item>

        <Form.Item
          label={t('시설 배치도')}
          name="site_layout_photo"
          className="mb-3"
          rules={[{ required: requiredForm.site_layout_photo, message: t('이미지 파일을 선택해주세요') }]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {photoFiles.site_layout_photo && (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={previewUrls.site_layout_photo}
                  alt="선택한 사진"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview
                />
                <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                  {photoFiles.site_layout_photo.name}
                </span>
              </div>
            )}
            {!photoFiles.site_layout_photo && uploadedImages.site_layout_photo && (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={
                    (() => {
                      const image = uploadedImages.site_layout_photo;
                      const s3Key = image?.s3_key || 
                                   (image?.images && image.images[0]?.s3_key);
                      return s3Key 
                        ? `${import.meta.env.VITE_STATIC_URL}/${s3Key}`
                        : image?.image_url || image?.url || '';
                    })()
                  }
                  alt="시설 배치도"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleDeleteImage('site_layout_photo')}
                  style={{ marginLeft: 8 }}
                >
                  {t('삭제')}
                </Button>
              </div>
            )}
            <Input 
              key={fileInputKeys.site_layout_photo}
              type="file" 
              accept=".jpg,.jpeg,.png" 
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && validateFileSize(file, 'site_layout_photo')) {
                  handleFileChange('site_layout_photo', file);
                } else if (file) {
                  // 파일 크기 초과 시 input 초기화
                  handleFileChange('site_layout_photo', null);
                  setFileInputKeys(prev => ({
                    ...prev,
                    site_layout_photo: prev.site_layout_photo + 1,
                  }));
                }
              }} 
            />
          </Space>
        </Form.Item>

        <Form.Item
          label={t('공정 프로세스 사진')}
          name="site_process_photo"
          className="mb-3"
          rules={[{ required: requiredForm.site_process_photo, message: t('이미지 파일을 선택해주세요') }]}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {photoFiles.site_process_photo && (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={previewUrls.site_process_photo}
                  alt="선택한 사진"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview
                />
                <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                  {photoFiles.site_process_photo.name}
                </span>
              </div>
            )}
            {!photoFiles.site_process_photo && uploadedImages.site_process_photo && (
              <div style={{ marginBottom: 8 }}>
                <Image
                  src={
                    (() => {
                      const image = uploadedImages.site_process_photo;
                      const s3Key = image?.s3_key || 
                                   (image?.images && image.images[0]?.s3_key);
                      return s3Key 
                        ? `${import.meta.env.VITE_STATIC_URL}/${s3Key}`
                        : image?.image_url || image?.url || '';
                    })()
                  }
                  alt="공정 프로세스 사진"
                  width={200}
                  height={150}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={() => handleDeleteImage('site_process_photo')}
                  style={{ marginLeft: 8 }}
                >
                  {t('삭제')}
                </Button>
              </div>
            )}
            <Input 
              key={fileInputKeys.site_process_photo}
              type="file" 
              accept=".jpg,.jpeg,.png" 
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && validateFileSize(file, 'site_process_photo')) {
                  handleFileChange('site_process_photo', file);
                } else if (file) {
                  // 파일 크기 초과 시 input 초기화
                  handleFileChange('site_process_photo', null);
                  setFileInputKeys(prev => ({
                    ...prev,
                    site_process_photo: prev.site_process_photo + 1,
                  }));
                }
              }} 
            />
          </Space>
        </Form.Item>

      </Form>
    </Modal>
  );
};

export default SitePhotoModal;